import React, { useContext, useEffect, useMemo, useState } from "react"
import { BottomSheetView } from "@gorhom/bottom-sheet"
import TextM from "../../common/components/text/TextM"
import FilledButtonM from "../../common/components/button/FilledButtonM"
import {
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { theme } from "../../common/theme/theme"
import { TabSelect } from "../../common/components/select/TabSelect"
import { DimensionsContext } from "../../common/utils/mobileUtils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { ChangePlanBottomModalSection as Section } from "./components/ChangePlanBottomModalSection"
import { SwitchComponent } from "../../tutor/application/personalInfos/ApplicationPersonalInfos"
import { BottomSheetModalM } from "../../common/components/modal/BottomSheetM"
import { useSubscriptionPricing } from "./hooks/useSubscriptionPricing"
import { getOfferPricing } from "./newSubscriptionUtils"
import { HoursAllocationSection } from "./HoursAllocationSection"
import { useNewSubscriptionStepper } from "../../common/hooks/subscription/useNewSubscriptionStepper"
import { studentClasses } from "../../data/constants/formConstants"
import { DatePickerModal } from "react-native-paper-dates"
import moment from "moment"
import IconButtonM from "../../common/components/button/IconButtonM"
import {
    SharedCourseAdvantagesModal,
} from "./SharedCourseAdvantagesModal"

// Classes that allow selecting more subjects than hours (3ème and lower)
export const CLASSES_ALLOW_MORE_SUBJECTS = studentClasses.slice(
    0,
    studentClasses.indexOf("3ème") + 1
)

export const PopularBadge = () => (
    <View
        style={{
            backgroundColor: theme.colors.accent,
            paddingHorizontal: 10,
            paddingVertical: 3,
            borderRadius: 10,
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
        }}
    >
        <FontAwesome5 name="star" solid color={theme.colors.text} />
        <TextM fontSize={9} fontWeight="Bold" color={theme.colors.text}>
            Le plus choisi
        </TextM>
    </View>
)

/**
 * Les deux modes de cours de create-subscription. Le funnel d'inscription ne
 * les lit plus : il vend les mêmes offres sous leurs noms de gamme (Masteur
 * Solo, Masteur Tandem — `inscription/constants/inscriptionFormats`).
 *
 * Les sous-titres disent le NOMBRE D'ÉLÈVES, pas une fourchette de groupe :
 * « 2-3 élèves ensemble » laissait un parent imaginer un cours collectif, alors
 * que le partage plafonne à un binôme. Formulation alignée sur celle de
 * l'activation ({@link ActivationComposerScreen}), où l'arbitrage a été pris.
 */
export const offerTypeOptions = [
    {
        icon: "users",
        label: "Cours partagé",
        value: "essential",
        description: "avec un autre élève",
        topChild: (
            <FontAwesome5 name="users" size={16} color={theme.colors.primary} />
        ),
        floatingBadge: <PopularBadge />,
    },
    {
        icon: "user-alt",
        label: "Cours particulier",
        value: "integral",
        description: "seul avec son tuteur",
        topChild: (
            <FontAwesome5
                name="user-alt"
                size={16}
                color={theme.colors.primary}
            />
        ),
    },
]

const engagementDurationOptions = [
    {
        label: "12",
        value: 12,
        description: "mois",
        floatingBadge: <PopularBadge />,
        bottomContent: (
            <TextM
                fontWeight="medium"
                color={theme.colors.success}
                fontSize={11}
                style={{ textAlign: "center", marginTop: 8 }}
            >
                ✓ Le 1er mois est sans engagement
            </TextM>
        ),
    },
]

export const ChangePlanBottomModal = ({
    schoolSubjects,
    closeModal,
    plan,
    setPlan,
    bottomModalRef,
    pricingTable,
    sessionsPerWeek,
    selfServeNoCommitmentHours,
    studentClass,
}) => {
    const { generateDefaultSubjectHoursAllocation } =
        useNewSubscriptionStepper()
    const dimensions = useContext(DimensionsContext)
    const [updatedPlan, setUpdatedPlan] = useState(plan)
    const [showAdvantagesModal, setShowAdvantagesModal] = useState(false)
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [hoursAllocationExpanded, setHoursAllocationExpanded] =
        useState(false)

    // Check if student can select more subjects than hours (3ème or lower)
    const allowMoreSubjectsThanHours =
        CLASSES_ALLOW_MORE_SUBJECTS.includes(studentClass)

    // Sans engagement, le self-serve ne commercialise qu'un palier par offre. La
    // règle vient du back (`Subscription::SELF_SERVE_NO_COMMITMENT_HOURS`), qui
    // refuse le checkout hors palier — elle arrive en heures par mois et se convertit
    // avec la même table de cadence que le reste de l'écran. Absente, on ne restreint
    // rien : la garde serveur reste le filet.
    const soldNoCommitmentHoursPerWeek = (offerType) => {
        const hoursPerMonth = selfServeNoCommitmentHours?.[offerType]
        return hoursPerMonth ? sessionsPerWeek?.[hoursPerMonth] ?? null : null
    }

    const handleValidatePlan = ({ commit }) => {
        return () => {
            if (commit) setPlan({ ...plan, ...updatedPlan })
            else setUpdatedPlan(plan)
            closeModal()
        }
    }

    const { hoursPerMonth, monthlyPrice, hourlyPrice, offerPricing } =
        useSubscriptionPricing({
            pricingTable,
            sessionsPerWeek,
            plan: updatedPlan,
        })

    // Calculate price majoration for no commitment
    const noCommitmentMajoration = useMemo(() => {
        const commitmentPricing = getOfferPricing({
            pricingTable,
            offerType: updatedPlan.offerType,
            sessionsPerWeek,
            withCommitment: true,
        })
        const noCommitmentPricing = getOfferPricing({
            pricingTable,
            offerType: updatedPlan.offerType,
            sessionsPerWeek,
            withCommitment: false,
        })
        // Le switch annonce la majoration du palier sur lequel le parent va
        // atterrir, pas celle du palier qu'il quitte en basculant.
        const comparedHoursPerWeek =
            soldNoCommitmentHoursPerWeek(updatedPlan.offerType) ??
            updatedPlan.hoursPerWeek
        const commitmentPrice =
            commitmentPricing?.[comparedHoursPerWeek]?.hourlyPrice || 0
        const noCommitmentPrice =
            noCommitmentPricing?.[comparedHoursPerWeek]?.hourlyPrice || 0
        return Math.round((noCommitmentPrice - commitmentPrice) * 100) / 100
    }, [
        pricingTable,
        updatedPlan.offerType,
        updatedPlan.hoursPerWeek,
        sessionsPerWeek,
        selfServeNoCommitmentHours,
    ])

    const subjectOptions = useMemo(() => {
        return schoolSubjects.map((subject) => ({
            label: subject.name,
            value: subject.id,
            labelIconImage: subject.icon,
        }))
    }, [schoolSubjects])

    const noCommitmentHoursPerWeek = soldNoCommitmentHoursPerWeek(
        updatedPlan.offerType
    )
    const hoursLockedByNoCommitment =
        !updatedPlan.withCommitment && noCommitmentHoursPerWeek !== null

    const hoursPerWeekOptions = useMemo(() => {
        return Object.values(offerPricing)
            .filter(
                (pricing) =>
                    !hoursLockedByNoCommitment ||
                    pricing.weeklyHours === noCommitmentHoursPerWeek
            )
            .map((pricing) => ({
                label: `${pricing.weeklyHours}h`,
                value: pricing.weeklyHours,
            }))
    }, [offerPricing, hoursLockedByNoCommitment, noCommitmentHoursPerWeek])

    const planWithHoursPerWeek = (currentPlan, hoursPerWeek) => {
        // Only limit subjects if not allowed to have more subjects than hours
        const subjectIds = allowMoreSubjectsThanHours
            ? currentPlan.subjectIds
            : currentPlan.subjectIds.slice(0, hoursPerWeek)

        return {
            ...currentPlan,
            hoursAllocation: generateDefaultSubjectHoursAllocation(
                subjectIds,
                hoursPerWeek
            ),
            hoursPerWeek,
            subjectIds,
        }
    }

    const onChangeHoursPerWeek = (hoursPerWeek) =>
        setUpdatedPlan(planWithHoursPerWeek(updatedPlan, hoursPerWeek))

    // Passer sans engagement — ou changer d'offre en l'étant déjà — ramène sur le
    // seul palier commercialisé en self-serve. Sans ce recalage, le parent partirait
    // au checkout sur un volume que le back refuse.
    const onChangePlan = (changes) => {
        const nextPlan = { ...updatedPlan, ...changes }
        const soldHoursPerWeek = nextPlan.withCommitment
            ? null
            : soldNoCommitmentHoursPerWeek(nextPlan.offerType)

        setUpdatedPlan(
            soldHoursPerWeek !== null &&
                nextPlan.hoursPerWeek !== soldHoursPerWeek
                ? planWithHoursPerWeek(nextPlan, soldHoursPerWeek)
                : nextPlan
        )
    }

    const onChangeSubjects = (subjectIds) => {
        // Reset allocation when subjects > hours (no distribution possible)
        const hoursAllocation =
            subjectIds.length > updatedPlan.hoursPerWeek
                ? {}
                : generateDefaultSubjectHoursAllocation(
                      subjectIds,
                      updatedPlan.hoursPerWeek
                  )
        setUpdatedPlan({ ...updatedPlan, subjectIds, hoursAllocation })
    }

    useEffect(() => {
        setUpdatedPlan(plan)
        setHoursAllocationExpanded(false)
    }, [plan])

    // Reset expanded state when allocation should no longer be shown
    useEffect(() => {
        const canShowAllocation =
            updatedPlan.subjectIds.length > 1 &&
            updatedPlan.subjectIds.length <= updatedPlan.hoursPerWeek &&
            updatedPlan.subjectIds.length !== updatedPlan.hoursPerWeek
        if (!canShowAllocation) {
            setHoursAllocationExpanded(false)
        }
    }, [updatedPlan.subjectIds.length, updatedPlan.hoursPerWeek])

    return (
        <BottomSheetModalM ref={bottomModalRef} height="90%" withPanning>
            <BottomSheetView
                style={[styles.container, { width: dimensions.width }]}
            >
                <View style={styles.headerIcon} />
                <View style={styles.header}>
                    <TextM fontSize={20} fontWeight="bold" center>
                        Choisissez votre formule
                    </TextM>
                </View>
                <ScrollView contentContainerStyle={styles.content}>
                    {/* OFFER TYPE */}
                    <Section
                        title="Mode de cours"
                        icon="users"
                        rightContent={
                            <IconButtonM
                                icon="info-circle"
                                size={20}
                                iconColor={theme.colors.primary}
                                onPress={() => setShowAdvantagesModal(true)}
                            />
                        }
                    >
                        <TabSelect
                            options={offerTypeOptions}
                            selected={updatedPlan.offerType}
                            onSelect={(offerType) =>
                                onChangePlan({ offerType })
                            }
                            cardStyle={{
                                flex: 1,
                                minWidth: dimensions.width > 800 ? 400 : 220,
                            }}
                        />
                    </Section>

                    {/* HOURS PER WEEK */}
                    <Section title="Heures par semaine" icon="clock">
                        <TabSelect
                            options={hoursPerWeekOptions}
                            selected={updatedPlan.hoursPerWeek}
                            onSelect={onChangeHoursPerWeek}
                            cardStyle={{ minWidth: 100, alignItems: "center" }}
                            containerStyle={
                                dimensions.width <= 600
                                    ? {
                                          marginHorizontal: -16,
                                          paddingHorizontal: 16,
                                      }
                                    : {}
                            }
                        />
                        {hoursLockedByNoCommitment && (
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                style={{ marginTop: 8 }}
                            >
                                L'offre sans engagement est disponible en{" "}
                                {noCommitmentHoursPerWeek}h par semaine.
                                Choisissez l'engagement 12 mois pour un autre
                                volume.
                            </TextM>
                        )}
                    </Section>

                    {/* SUBJECTS */}
                    <Section title="Matières souhaitées" icon="book">
                        <TabSelect
                            multiple
                            options={subjectOptions}
                            selected={updatedPlan.subjectIds}
                            onSelect={onChangeSubjects}
                            cardStyle={{ borderRadius: 30 }}
                            maxSelection={
                                allowMoreSubjectsThanHours
                                    ? null
                                    : updatedPlan.hoursPerWeek
                            }
                            containerStyle={
                                dimensions.width <= 600
                                    ? {
                                          marginHorizontal: -16,
                                          paddingHorizontal: 16,
                                      }
                                    : {}
                            }
                        />
                    </Section>

                    {/* SUBJECT HOURS ALLOCATION */}
                    {/* Show only when: nb subjects <= nb hours AND nb subjects > 1 AND nb subjects != nb hours */}
                    {updatedPlan.subjectIds.length > 1 &&
                        updatedPlan.subjectIds.length <=
                            updatedPlan.hoursPerWeek &&
                        updatedPlan.subjectIds.length !==
                            updatedPlan.hoursPerWeek &&
                        (hoursAllocationExpanded ? (
                            <Section
                                title="Heures par matière"
                                icon="balance-scale"
                            >
                                <HoursAllocationSection
                                    allocation={updatedPlan.hoursAllocation}
                                    setAllocation={(hoursAllocation) =>
                                        setUpdatedPlan({
                                            ...updatedPlan,
                                            hoursAllocation,
                                        })
                                    }
                                    selectedSubjects={schoolSubjects.filter(
                                        (subject) =>
                                            updatedPlan.subjectIds.includes(
                                                subject.id
                                            )
                                    )}
                                    hoursPerWeek={updatedPlan.hoursPerWeek}
                                />
                            </Section>
                        ) : (
                            <TouchableOpacity
                                onPress={() => setHoursAllocationExpanded(true)}
                                style={styles.optionalLink}
                            >
                                <TextM
                                    fontSize={12}
                                    color={theme.colors.light}
                                    fontWeight="Medium"
                                >
                                    Répartir les heures entre les matières
                                    (optionnel)
                                </TextM>
                                <FontAwesome5
                                    name="chevron-right"
                                    size={10}
                                    color={theme.colors.light}
                                />
                            </TouchableOpacity>
                        ))}

                    {/* COMMITMENT DURATION */}
                    <Section title="Durée d'engagement" icon="clock">
                        <TabSelect
                            options={engagementDurationOptions}
                            selected={updatedPlan.engagementDuration}
                            onSelect={(engagementDuration) =>
                                onChangePlan({
                                    engagementDuration,
                                    withCommitment: true,
                                })
                            }
                            cardStyle={{
                                flex: 1,
                                alignItems: "center",
                            }}
                            disabled={!updatedPlan.withCommitment}
                        />

                        <TouchableOpacity
                            activeOpacity={0.7}
                            onPress={() =>
                                onChangePlan({
                                    withCommitment: !updatedPlan.withCommitment,
                                    engagementDuration:
                                        updatedPlan.withCommitment ? 0 : 12,
                                })
                            }
                        >
                            <SwitchComponent
                                label={`Je préfère zéro engagement${
                                    noCommitmentMajoration > 0
                                        ? ` (+${Math.round(
                                              noCommitmentMajoration
                                          )}€/h)`
                                        : ""
                                }`}
                                value={!updatedPlan.withCommitment}
                                containerStyle={{
                                    width: "100%",
                                    borderWidth: 0,
                                    marginTop: 0,
                                    marginBottom: 0,
                                    paddingVertical: 10,
                                }}
                                labelStyle={{
                                    color: theme.colors.grey,
                                    fontSize: 12,
                                }}
                                setValue={(noEngagement) =>
                                    onChangePlan({
                                        withCommitment: !noEngagement,
                                        engagementDuration: noEngagement
                                            ? 0
                                            : 12,
                                    })
                                }
                                renderAdditionalComponent={() =>
                                    !updatedPlan.withCommitment ? (
                                        <TextM
                                            fontSize={11}
                                            color={theme.colors.light}
                                            style={{ paddingHorizontal: 10 }}
                                        >
                                            Résiliable à tout moment
                                        </TextM>
                                    ) : null
                                }
                            />
                        </TouchableOpacity>
                    </Section>

                    {/* START DATE */}
                    <Section title="Date de début" icon="calendar-alt">
                        <TouchableOpacity
                            onPress={() => setShowDatePicker(true)}
                            style={styles.datePickerButton}
                        >
                            <TextM fontWeight="Medium">
                                {moment(updatedPlan.startDate).format(
                                    "D MMMM YYYY"
                                )}
                            </TextM>
                            <FontAwesome5
                                name="chevron-down"
                                size={12}
                                color={theme.colors.grey}
                            />
                        </TouchableOpacity>
                    </Section>
                </ScrollView>

                {/* FOOTER */}
                <View style={styles.footer}>
                    <View style={[styles.row, styles.footerCard]}>
                        <View>
                            <TextM
                                fontWeight="medium"
                                color={theme.colors.light}
                            >
                                Tarif horaire
                            </TextM>
                            <TextM
                                fontWeight="medium"
                                color={theme.colors.light}
                            >
                                {hoursPerMonth} h par mois à {monthlyPrice}{" "}
                                €/mois
                            </TextM>
                        </View>
                        <View style={[styles.row, styles.priceRow]}>
                            <TextM
                                fontWeight="Bold"
                                fontSize={24}
                                color={theme.colors.primary}
                            >
                                {Math.round(hourlyPrice)}€
                            </TextM>
                            <TextM>/h</TextM>
                        </View>
                    </View>

                    {/* Buttons */}
                    <View style={styles.row}>
                        <FilledButtonM
                            label="Annuler"
                            onPress={handleValidatePlan({ commit: false })}
                            mode="outline"
                            style={styles.button}
                        />
                        <FilledButtonM
                            label="Continuer"
                            onPress={handleValidatePlan({ commit: true })}
                            style={styles.button}
                        />
                    </View>
                </View>
            </BottomSheetView>

            {/* Advantages Modal — partagée avec le funnel d'inscription */}
            <SharedCourseAdvantagesModal
                isVisible={showAdvantagesModal}
                onClose={() => setShowAdvantagesModal(false)}
            />

            {/* Date Picker Modal */}
            <DatePickerModal
                locale="fr"
                mode="single"
                startWeekOnMonday
                visible={showDatePicker}
                onDismiss={() => setShowDatePicker(false)}
                date={new Date(updatedPlan.startDate)}
                onConfirm={({ date }) => {
                    setShowDatePicker(false)
                    if (date) {
                        setUpdatedPlan({
                            ...updatedPlan,
                            startDate: moment(date).format("YYYY-MM-DD"),
                        })
                    }
                }}
                validRange={{
                    startDate: new Date(
                        new Date().setDate(new Date().getDate() + 1)
                    ),
                    // Max date is the 1st of next month
                    endDate: moment().add(1, "month").startOf("month").toDate(),
                }}
            />
        </BottomSheetModalM>
    )
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
    },
    container: {
        flex: 1,
        borderRadius: 10,
        overflow: "hidden",
        marginHorizontal: "auto",
    },
    headerIcon: {
        width: 100,
        height: 8,
        backgroundColor: theme.colors.lightMore,
        marginTop: 16,
        marginHorizontal: "auto",
        borderRadius: 4,
    },
    header: {
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
    },
    content: {
        padding: 16,
        gap: 20,
    },
    footer: {
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        gap: 16,
    },
    footerCard: {
        backgroundColor: theme.colors.lightMoreTransparent,
        padding: 16,
        borderRadius: 10,
    },
    priceRow: {
        alignItems: "flex-end",
        gap: 4,
    },
    optionalLink: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    button: {
        flex: 1,
        minWidth: 100,
        marginBottom: 0,
    },
    datePickerButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.colors.white,
        padding: 15,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: theme.colors.light,
    },
})
